/**
 * The three URLs the public `/delete-your-data` sign-in round trip is built from.
 *
 * These are shared by the Bun route table, the authorize and callback handlers,
 * and the picker row the patient clicks. Every EHR validates `redirect_uri`
 * byte-for-byte, so the callback path is the one already registered with the
 * vendors and must change only deliberately.
 */

/** The public page the whole flow starts and ends on. */
export const DELETE_PATIENT_DATA_PAGE_PATH = "/delete-your-data";

/** Route that starts a portal sign-in. Takes `?fhirBaseUrl=`. */
export const DELETE_PATIENT_DATA_AUTHORIZE_PATH = "/api/delete-patient-data/authorize";

/**
 * Route the EHR sends the patient back to. It is deliberately the same path the
 * patient sign-in uses, because that path is registered against the Medblocks
 * client ids this flow borrows and vendors compare `redirect_uri` byte for byte.
 * The handler tells the two apart by the encrypted `mb_dpd_state` cookie.
 */
export const DELETE_PATIENT_DATA_CALLBACK_PATH = "/api/auth/smart/callback";

/**
 * Builds the href for one picker row, which is the link that starts a sign-in at
 * that health system.
 *
 * @param fhirBaseUrl The health system's FHIR base URL, as `searchSources` returns it.
 * @returns A same-origin path with the source encoded as a query parameter.
 */
export function deletePatientDataAuthorizePath(fhirBaseUrl: string): string {
  return `${DELETE_PATIENT_DATA_AUTHORIZE_PATH}?fhirBaseUrl=${encodeURIComponent(fhirBaseUrl)}`;
}

/** How long one portal login stays good for. */
export const DELETE_PATIENT_DATA_SESSION_TTL_MINUTES = 30;

/** Name of the cookie carrying the `delete_patient_data_session` id. */
export const DELETE_PATIENT_DATA_COOKIE = "mb_delete_patient_data";

/** Name of the cookie carrying the encrypted OAuth state of a portal sign-in. */
export const DELETE_PATIENT_DATA_STATE_COOKIE = "mb_dpd_state";

/**
 * Environment variable that sets how long a confirmed delete waits before the
 * rows are purged, in whole days. Out of range or unparseable values fall back
 * to the default.
 */
export const DELETE_PATIENT_DATA_GRACE_DAYS_FLAG = "DELETE_PATIENT_DATA_GRACE_DAYS";

/** The wait applied when the variable is unset or unusable. */
export const DELETE_PATIENT_DATA_GRACE_DAYS_DEFAULT = 7;

/** The shortest wait the variable may ask for. Zero deletes at once. */
export const DELETE_PATIENT_DATA_GRACE_DAYS_MIN = 0;

/** The longest wait the variable may ask for. */
export const DELETE_PATIENT_DATA_GRACE_DAYS_MAX = 7;
